import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileUp, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { parseCsv, buildTransactions, detectColumn } from "@/lib/finance";
import { Money } from "@/components/shared";
import { toast } from "sonner";

const STEPS = ["Upload", "Map columns", "Preview", "Done"];

export function CsvImportWizard({ open, onOpenChange, onConfirm, rules }) {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const [account, setAccount] = useState("Imported");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({ date: -1, description: -1, amount: -1, debit: null, credit: null });
  const [useDebitCredit, setUseDebitCredit] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setStep(0); setFileName(""); setHeaders([]); setRows([]); setUseDebitCredit(false);
    setMapping({ date: -1, description: -1, amount: -1, debit: null, credit: null });
  };

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const { headers: h, rows: r } = parseCsv(String(reader.result || ""));
      if (!h.length || !r.length) { toast.error("Could not read any rows from this CSV"); return; }
      setHeaders(h); setRows(r);
      const amtIdx = detectColumn(h, ["amount", "value"]);
      const debitIdx = detectColumn(h, ["debit", "withdrawal"]);
      const creditIdx = detectColumn(h, ["credit", "deposit"]);
      const hasDC = amtIdx === -1 && (debitIdx !== -1 || creditIdx !== -1);
      setUseDebitCredit(hasDC);
      setMapping({
        date: detectColumn(h, ["date", "posted", "time"]),
        description: detectColumn(h, ["description", "name", "memo", "payee", "details"]),
        amount: amtIdx,
        debit: hasDC ? debitIdx : null,
        credit: hasDC ? creditIdx : null,
      });
      setStep(1);
    };
    reader.readAsText(file);
  };

  const colOptions = [{ value: "-1", label: "— None —" }, ...headers.map((h, i) => ({ value: String(i), label: h || `Column ${i + 1}` }))];
  const preview = step >= 2 ? buildTransactions(rows, mapping, account, rules) : [];
  const canMap = mapping.date >= 0 && mapping.description >= 0 && (useDebitCredit ? (mapping.debit != null || mapping.credit != null) : mapping.amount >= 0);

  const confirm = () => {
    const txns = buildTransactions(rows, mapping, account, rules);
    if (!txns.length) { toast.error("No transactions parsed — check your column mapping"); return; }
    onConfirm(txns);
    setStep(3);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[var(--surface-2)] border border-[color:var(--border-subtle)] max-w-2xl" data-testid="csv-wizard">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[var(--text)]">Import transactions</DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">Map your bank's columns, preview, then import.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`grid place-items-center h-6 w-6 rounded-full text-[11px] font-semibold ${i <= step ? "bg-[var(--green)] text-[#f7fbf9]" : "bg-[color:rgba(23,35,31,0.08)] text-[var(--text-muted)]"}`}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`text-xs ${i === step ? "text-[var(--text)] font-medium" : "text-[var(--text-muted)]"}`}>{s}</span>
              {i < STEPS.length - 1 && <span className="w-6 h-px bg-[color:var(--border-subtle)]" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="py-4">
            <div className="mb-4"><Label className="text-xs text-[var(--text-muted)]">Assign to account</Label>
              <Input value={account} onChange={(e) => setAccount(e.target.value)} className="bg-white border-[color:var(--border-subtle)] mt-1" data-testid="wizard-account-input" /></div>
            <button onClick={() => fileRef.current?.click()} data-testid="wizard-dropzone"
              className="w-full rounded-[12px] border-2 border-dashed border-[color:var(--border-strong)] bg-[var(--surface-3)] py-10 flex flex-col items-center gap-2 hover:bg-[color:rgba(31,115,85,0.05)] transition-colors">
              <FileUp className="h-8 w-8 text-[var(--green)]" />
              <span className="text-sm font-medium text-[var(--text)]">Choose a CSV file</span>
              <span className="text-xs text-[var(--text-faint)]">Bank statements & exports supported</span>
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onFile} data-testid="wizard-file-input" />
          </div>
        )}

        {step === 1 && (
          <div className="py-2 space-y-4">
            <p className="text-sm text-[var(--text-muted)]">Detected <b>{headers.length}</b> columns and <b>{rows.length}</b> rows in <b>{fileName}</b>. Match them below:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MapSelect label="Date column" value={mapping.date} onChange={(v) => setMapping({ ...mapping, date: v })} options={colOptions} testid="map-date" />
              <MapSelect label="Description column" value={mapping.description} onChange={(v) => setMapping({ ...mapping, description: v })} options={colOptions} testid="map-description" />
              {!useDebitCredit ? (
                <MapSelect label="Amount column" value={mapping.amount} onChange={(v) => setMapping({ ...mapping, amount: v })} options={colOptions} testid="map-amount" />
              ) : (
                <>
                  <MapSelect label="Debit column" value={mapping.debit ?? -1} onChange={(v) => setMapping({ ...mapping, debit: v < 0 ? null : v })} options={colOptions} testid="map-debit" />
                  <MapSelect label="Credit column" value={mapping.credit ?? -1} onChange={(v) => setMapping({ ...mapping, credit: v < 0 ? null : v })} options={colOptions} testid="map-credit" />
                </>
              )}
            </div>
            <button onClick={() => { setUseDebitCredit(!useDebitCredit); setMapping({ ...mapping, amount: -1, debit: null, credit: null }); }}
              className="text-xs text-[var(--green)] hover:underline" data-testid="toggle-debit-credit">
              {useDebitCredit ? "Use a single Amount column instead" : "My bank uses separate Debit / Credit columns"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="py-2">
            <p className="text-sm text-[var(--text-muted)] mb-3">Previewing <b>{preview.length}</b> transactions (first 8 shown):</p>
            <div className="rounded-[12px] border border-[color:var(--border-subtle)] overflow-hidden max-h-[320px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.slice(0, 8).map((t) => (
                    <TableRow key={t.id}><TableCell className="text-[var(--text-muted)]">{t.date}</TableCell><TableCell className="font-medium">{t.name}</TableCell><TableCell className="text-[var(--text-muted)]">{t.category}</TableCell><TableCell className="text-right"><Money value={t.amount} decimals={2} /></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="py-8 flex flex-col items-center text-center">
            <span className="grid place-items-center h-14 w-14 rounded-full bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] mb-3"><Check className="h-7 w-7" /></span>
            <p className="font-serif-display text-lg text-[var(--text)]">Import complete</p>
            <p className="text-sm text-[var(--text-muted)] mt-1">{preview.length} transactions added to your Cash flow.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 1 && <Button variant="outline" onClick={() => setStep(0)} className="border-[color:var(--border-subtle)]"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>}
          {step === 2 && <Button variant="outline" onClick={() => setStep(1)} className="border-[color:var(--border-subtle)]"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>}
          {step === 1 && <Button onClick={() => setStep(2)} disabled={!canMap} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="wizard-next-button">Preview<ArrowRight className="h-4 w-4 ml-1" /></Button>}
          {step === 2 && <Button onClick={confirm} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="wizard-confirm-button">Import {preview.length}<Check className="h-4 w-4 ml-1" /></Button>}
          {step === 3 && <Button onClick={() => close(false)} className="bg-[var(--green)] text-[#f7fbf9]" data-testid="wizard-done-button">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MapSelect = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[var(--text-muted)]">{label}</Label>
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="bg-white border-[color:var(--border-subtle)]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);
